/**
 * `fetch` do browser tolerante ao BFF que ainda não subiu.
 *
 * `scripts/dev.sh` sobe o Vite antes do BFF (o proxy então responde 500 sem
 * corpo) e a Cloud Function do BFF escala a zero em produção. A PRIMEIRA carga
 * da página pegava esse intervalo e o bootstrap do React morria numa tela branca
 * com o texto do erro — recarregar "resolvia" só porque o BFF tinha subido.
 *
 * Só repete o que é seguro repetir: falha de rede e 5xx em requisição que NÃO
 * escreve (GET/HEAD). Escrita nunca repete aqui — o BFF já absorve o cold start
 * do service (`src/shared/upstream-fetch.ts`), e repetir um POST que talvez
 * tenha chegado duplicaria cadastro ou biometria.
 */

export type RetryPolicy = {
  maxAttempts: number;
  /** Passada a janela, nenhuma tentativa NOVA começa. */
  retryWindowMs: number;
  baseBackoffMs: number;
  maxBackoffMs: number;
};

/**
 * A janela cobre o tempo que o BFF leva para começar a escutar (`nest start` do
 * `dev.sh`, cold start da Function). Em teste só as ESPERAS encolhem — quantas
 * tentativas e quais casos repetem continuam iguais, que é o que os testes
 * verificam (mesmo recurso usado por `helpers/selfieConfig.ts`).
 */
export const SAFE_RETRY_POLICY: RetryPolicy =
  import.meta.env.MODE === 'test'
    ? { maxAttempts: 6, retryWindowMs: 100, baseBackoffMs: 1, maxBackoffMs: 1 }
    : { maxAttempts: 6, retryWindowMs: 12_000, baseBackoffMs: 300, maxBackoffMs: 3_000 };

const SAFE_METHODS = new Set(['GET', 'HEAD']);

function isSafeMethod(init?: RequestInit): boolean {
  return SAFE_METHODS.has((init?.method ?? 'GET').toUpperCase());
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function backoffFor(attempt: number, policy: RetryPolicy): number {
  return Math.min(policy.baseBackoffMs * 2 ** (attempt - 1), policy.maxBackoffMs);
}

/**
 * 5xx só vale a pena repetir quando veio da INFRAESTRUTURA — BFF fora do ar.
 * O BFF sempre responde JSON (`AllExceptionsFilter`), e o 502 dele já é o
 * resultado de ele mesmo ter repetido no service; repetir de novo aqui só
 * multiplicaria a espera do convidado. Quem responde sem JSON é o proxy do Vite
 * (500 text/plain vazio, alvo fora) ou a borda do Firebase (HTML) — aí sim o
 * BFF ainda não subiu e a próxima tentativa tende a pegá-lo de pé.
 */
export function isUpstreamUnavailable(response: Response): boolean {
  if (response.status < 500) return false;
  return !(response.headers.get('content-type') ?? '').includes('json');
}

export async function fetchWithRetry(
  input: string,
  init?: RequestInit,
  policy: RetryPolicy = SAFE_RETRY_POLICY,
): Promise<Response> {
  if (!isSafeMethod(init)) {
    return fetch(input, init);
  }

  const startedAt = Date.now();
  let attempt = 0;

  for (;;) {
    attempt += 1;
    const canRetryAgain =
      attempt < policy.maxAttempts &&
      Date.now() - startedAt + backoffFor(attempt, policy) <= policy.retryWindowMs;

    try {
      const response = await fetch(input, init);
      if (canRetryAgain && isUpstreamUnavailable(response)) {
        void response.body?.cancel();
        await sleep(backoffFor(attempt, policy));
        continue;
      }
      return response;
    } catch (error) {
      if (!canRetryAgain) throw error;
      await sleep(backoffFor(attempt, policy));
    }
  }
}
